import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";

/**
 * Setup walkthrough for connecting a store via a Shopify Dev Dashboard app.
 *
 * Shopify no longer allows creating custom apps inside the store admin — new
 * apps are created in the Dev Dashboard and only expose a Client ID + Client
 * Secret (no access token in the UI). The backend exchanges those for the
 * access token and keeps it refreshed automatically, so the merchant only ever
 * pastes the two credentials below.
 */
export function ShopifySetupInstructions() {

  return (
    <div className="space-y-4">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="step1">
          <AccordionTrigger>
            <span className="font-semibold">Step 1: Create an App in the Shopify Dev Dashboard</span>
          </AccordionTrigger>
          <AccordionContent>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Open the <strong>Shopify Dev Dashboard</strong> (log in with the account that owns your store)</li>
              <li>Click <strong>"Create app"</strong></li>
              <li>Name it: <code className="bg-muted px-2 py-1 rounded">"ThreadHeads IMS"</code></li>
            </ol>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => window.open("https://dev.shopify.com", "_blank")}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open Shopify Dev Dashboard
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="step2">
          <AccordionTrigger>
            <span className="font-semibold">Step 2: Configure API Scopes</span>
          </AccordionTrigger>
          <AccordionContent>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>In the app, open <strong>Configuration</strong> → <strong>Admin API integration</strong></li>
              <li>Select these scopes:
                <ul className="list-none ml-6 mt-2 space-y-1">
                  <li>✓ <code className="bg-muted px-2 py-1 rounded text-xs">read_orders</code></li>
                  <li>✓ <code className="bg-muted px-2 py-1 rounded text-xs">read_products</code></li>
                  <li>✓ <code className="bg-muted px-2 py-1 rounded text-xs">read_inventory</code></li>
                </ul>
              </li>
              <li className="mt-2">Save, then <strong>install the app on your store</strong></li>
            </ol>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="step3">
          <AccordionTrigger>
            <span className="font-semibold">Step 3: Copy the Client ID &amp; Client Secret</span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm">
              In the app's <strong>Overview</strong> (or <strong>Settings</strong>) you'll find the{' '}
              <strong>Client ID</strong> and <strong>Client Secret</strong>. Paste both into the form:
              that's everything we need. We take care of turning them into an access token and keeping
              it fresh, so there's nothing else to copy and nothing that expires on you.
            </p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="step4">
          <AccordionTrigger>
            <span className="font-semibold">Step 4: Get Your Shopify Domain</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 text-sm">
              <p>Your Shopify domain is your store URL, which looks like:</p>
              <code className="block bg-muted px-3 py-2 rounded">yourstore.myshopify.com</code>
              <p className="text-muted-foreground">
                You can find this in your Shopify admin URL bar.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
